<script setup lang="ts">
import { computed, useSlots } from 'vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import type { AnalyticsData } from '@/tracking/analytics/analyticsTracker.ts'

const props = withDefaults(defineProps<{
  isAccordionLayout?: boolean
  eventContext?: AnalyticsData
  /** Names the goal's level above its text, as a saved goal page does. */
  header?: string
}>(), {
  isAccordionLayout: false,
  eventContext: () => ({})
})

defineSlots<{
  default(): unknown
  translation?(): unknown
  footer?(): unknown
}>()

getAnalyticsTracker().getChildContext(props.eventContext)

const slots = useSlots()
const hasFooter = computed(() => Boolean(slots.footer))

const SECTION_BORDER_CLASSES = 'border border-gray-300 shadow-sm'

// Vue 2 drew a headed goal as one framed panel, with no borders of its own inside the frame.
const isFramedWithHeader = computed(() => !props.isAccordionLayout && Boolean(props.header))

const panelClasses = computed(() => {
  if (props.isAccordionLayout) {
    return 'mt-2 rounded-b-xs border border-gray-600 shadow-sm'
  }
  if (isFramedWithHeader.value) {
    return 'border border-gray-600 bg-white shadow-[0_1px_1px_rgba(0,0,0,0.05)]'
  }
  return 'rounded-xs shadow-sm'
})
const bodyClasses = computed(() => {
  if (props.isAccordionLayout) {
    return 'p-4'
  }
  if (isFramedWithHeader.value) {
    return 'p-[15px]'
  }
  return `p-4 ${SECTION_BORDER_CLASSES} rounded-t-xs border-b-0`
})
const footerSectionClasses = computed(() =>
  props.isAccordionLayout || isFramedWithHeader.value ? '' : '[&>*]:border-x [&>*]:border-gray-300 [&>*]:shadow-sm'
)
</script>

<template>
  <div class="mb-5" :class="panelClasses" data-testid="big-goal">
    <h2 v-if="header" class="m-0 px-[15px] py-2.5 text-text-md font-regular leading-[1.1] text-blue-400" data-testid="big-goal-header">{{ header }}</h2>
    <div class="text-text-2xl font-regular leading-8" :class="bodyClasses" data-testid="big-goal-body">
      <slot />
      <slot name="translation" />
    </div>
    <div
      v-if="hasFooter"
      class="[&>*]:relative [&>*]:border-b-2 [&>*]:border-b-gray-400 [&>*]:bg-white"
      :class="footerSectionClasses"
      data-testid="big-goal-footer"
    >
      <slot name="footer" />
    </div>
  </div>
</template>
